import React, { useMemo, useState } from 'react';

import { useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';

import { Autocomplete, CircularProgress, ListItemText, MenuItem, TextField } from '@mui/material';

import { SEARCH_DEBOUNCE_MS } from '~/consts/tours';

import useDebounced from '~/hooks/useDebounced';

import SearchService, { TypeaheadBrand } from '~/services/SearchService';

import { RELEVANT_TYPEAHEAD_TYPES } from '../TourDetails/RJSF/LELocationSearchWidget';

export interface NativeTourPlace {
  name: string | null;
  country: string | null;
}

interface Props {
  label: string;
  value: NativeTourPlace;
  onChange: (value: NativeTourPlace) => void;
}

interface PlaceOption {
  key: string;
  name: string;
  country: string | null;
  subtitle?: string;
}

const MIN_QUERY_LENGTH = 2;
const RESULTS_LIMIT = 20;

// As svc-tour reads the tour form's location: the first part is the place, the last the country.
function countryOf(secondaryText: string | undefined): string | null {
  const last = secondaryText?.split(',').pop()?.trim();
  const country = last?.replace(/\s*\([^)]*\)\s*$/, '').trim();
  return country ? country : null;
}

/** A start or end place, searched in svc-search and saved as its name and country. */
export default function NativeTourPlaceField({ label, value, onChange }: Props) {
  const tenant = useSelector<App.State, App.Tenant>((state) => state.tenant);
  const [inputValue, setInputValue] = useState('');
  const query = useDebounced(inputValue.trim(), SEARCH_DEBOUNCE_MS);

  const { data: results = [], isFetching } = useQuery({
    queryKey: ['native-tour-place-search', query, tenant?.brand],
    queryFn: async () =>
      (
        await SearchService.typeahead(query, {
          type: RELEVANT_TYPEAHEAD_TYPES,
          brand: (tenant?.brand as TypeaheadBrand) ?? 'luxuryescapes',
          region: 'AU',
          limit: RESULTS_LIMIT,
          priority: 'current',
        })
      ).result,
    enabled: query.length >= MIN_QUERY_LENGTH,
  });

  const selected = useMemo<PlaceOption | null>(
    () =>
      value.name
        ? { key: 'current', name: value.name, country: value.country, subtitle: value.country ?? undefined }
        : null,
    [value],
  );
  const options = useMemo<Array<PlaceOption>>(
    () =>
      results.map((result) => ({
        key: result.id,
        name: result.format.mainText,
        country: countryOf(result.format.secondaryText) ?? result.format.mainText,
        subtitle: result.format.secondaryText,
      })),
    [results],
  );

  return (
    <Autocomplete
      value={selected}
      onChange={(_event, option) => onChange({ name: option?.name ?? null, country: option?.country ?? null })}
      inputValue={inputValue}
      onInputChange={(_event, next) => setInputValue(next)}
      options={options}
      filterOptions={(all) => all}
      getOptionLabel={(option) => option.name}
      isOptionEqualToValue={(option, current) => option.name === current.name && option.country === current.country}
      loading={isFetching}
      noOptionsText={query.length < MIN_QUERY_LENGTH ? 'Type to search' : 'No places found'}
      renderOption={(props, option) => (
        <MenuItem {...props} key={option.key} dense>
          <ListItemText primary={option.name} secondary={option.subtitle} />
        </MenuItem>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          helperText={value.country ? `Country: ${value.country}` : 'Search for a city, region or place'}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {isFetching ? <CircularProgress size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}
